import { twMerge } from 'tailwind-merge';
import { useAppState } from '@/providers/AppStateProvider';
import { useString } from '@/hooks/useString';
import { useToggle } from '@/hooks/useToggle';
import { downloadFileCollection } from '@/utils/files';
import IconButton from '@/components/IconButton';
import Tooltip from '@/components/Tooltip';
import DownloadToaster from '@/toasters/DownloadToaster';
import { DownloadIcon } from '@/icons';
import { classes } from './utils';

export type DownloadButtonProps = {
  variant?: 'default' | 'aurora';
  className?: string;
  fileCollectionId: number;
};

export default function DownloadButton({
  variant,
  className,
  fileCollectionId,
}: DownloadButtonProps) {
  const { onApiError } = useAppState();
  const [downloadMessage, setDownloadMessage] = useString(
    'Preparing collection...'
  );
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();

  const handleDownload = async () => {
    try {
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      await downloadFileCollection(fileCollectionId, setDownloadMessage);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading collection');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  return (
    <>
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message={downloadMessage}
        onClose={handleCloseDownloadToaster}
      />
      <Tooltip arrow title="Download all">
        <IconButton
          disabled={isPreparingDownload}
          className={twMerge(
            variant === 'aurora' && classes.auroraButton,
            className
          )}
          aria-label="download collection"
          onClick={handleDownload}>
          <DownloadIcon />
        </IconButton>
      </Tooltip>
    </>
  );
}
